<template>
  <div class="wishcard__editor" :class="`wishcard__editor--${card.size}`">
    <div
      class="wishcard__field"
      :style="bgStyles"
    >
      <p
        v-if="showPlaceholder"
        class="wishcard__placeholder"
        :style="placeholderStyle"
      >
        <strong>{{ $t('wishcard.placeholder') + (isHandwritten ? ' *' : '') }}</strong><br/>{{ $t('wishcard.placeholder_note') }}
      </p>

      <textarea
        class="wishcard__input"
        name="card_text"
        :placeholder="showDefaultPlaceholder ? `${$t('wishcard.placeholder')} ${$t('wishcard.placeholder_note')}` : null"
        :rows="maxLines"
        data-limit="small"
        @input="validate"
        ref="textarea"
        v-text="cardText"
        @focus="focused = true"
        @blur="focused = false"
      />
      <div
        v-if="showLayeredImage"
        class="wishcard__layered-image"
        :style="imageStyles"
      />
    </div>
    <p
      v-if="isHandwritten"
      class="wishcard__handwritten-note"
      v-text="`* ${$t('wishcard.handwritten_note')}`"
    />
    <p
      v-if="showMaxLinesError"
      class="wishcard__validation"
      v-text="$t('wishcard.max_lines_error')"
    />
    <p
      v-if="showMaxLengthError"
      class="wishcard__validation"
      v-text="$t('wishcard.max_length', { maxLength })"
    />
  </div>
</template>

<script>
import {countLines, setSmallCardSize} from '../../../js/includes/textarea';

export default {
  name: 'TgWishcardTextarea',

  props: {
    maxLength: {
      type: Number,
      required: false,
      default: 320
    },
    maxLines: {
      type: Number,
      required: false,
      default: 9
    },
    cardText: {
      type: String,
      required: true,
      default: ''
    },
    card: {
      type: [Object, null],
      required: true,
      default: null
    },
    showLayeredImage: {
      type: Boolean,
      required: false,
      default: false
    },
    showDefaultPlaceholder: {
      type: Boolean,
      required: false,
      default: true
    },
    fontSizeRatio: {
      type: Number,
      required: false,
      default: null
    },
    lineHeightRatio: {
      type: Number,
      required: false,
      default: null
    }
  },

  data() {
    return {
      timeoutId: null,
      showMaxLinesError: false,
      showMaxLengthError: false,
      fontSize: '13px',
      focused: false,
      viewport: null,
      viewportContent: null,
      targetNode: null,
      observer: null
    }
  },

  methods: {
    onWindowResize() {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.updateSizes()
      }, 250);
    },

    updateSizes() {
      setSmallCardSize(this.$refs.textarea, this.fontSizeRatio, this.lineHeightRatio);

      this.fontSize = this.$refs.textarea.style.fontSize || '13px'
    },

    dispatchEvent(name, detail) {
      window.dispatchEvent(new CustomEvent(name, {detail, cancelable: true}));
    },

    validate() {
      const lines = countLines(this.$refs.textarea);
      const card = this.card;

      this.showMaxLinesError = lines > this.maxLines;
      this.showMaxLengthError = this.$refs.textarea.value.length > this.maxLength;

      const validationState = {
        tooManyLines: this.showMaxLinesError,
        tooManyCharacters: this.showMaxLengthError,
        card
      }

      const dispatchEvent = this.showMaxLinesError || this.showMaxLengthError
        ? 'wishcard:validation:invalid'
        : 'wishcard:validation:valid'

      this.$emit('validate', validationState);

      // Required for legacy monolith functionality
      this.dispatchEvent('wishcard:changed', {card});
      this.dispatchEvent(dispatchEvent, validationState);
    },

    focus() {
      this.viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');
    },

    blur() {
      this.viewport.setAttribute('content', this.viewportContent);
    },

    setViewportTransform(elm) {
      elm?.target?.classList.contains('hidden') ? this.blur() : this.focus()
    }
  },

  computed: {
    showPlaceholder() {
      let textLength = this.$refs?.textarea?.value?.length;
      if (textLength === undefined) {
        textLength = this.cardText.length;
      }

      return this.focused === false
        && this.showDefaultPlaceholder === false
        && textLength === 0
    },

    bgStyles() {
      const styles = {}
      if (this.hasBgImage) {
        styles['background-image'] = `url('${this.card.bgImage}')`
      }
      return styles;
    },

    placeholderStyle() {
      return {
        'font-size': this.fontSize
      }
    },

    hasBgImage() {
      return this.card && this.card.bgImage
    },

    imageStyles() {
      const styles = {}
      if (this.hasImage) {
        styles['background-image'] = `url('${this.card.image}')`
      }
      return styles;
    },

    hasImage() {
      return this.card && this.card.image
    },

    isHandwritten() {
      return this.card && this.card.isHandwritten
    }
  },

  created() {
    this.viewport = document.querySelector("meta[name=viewport]");
    this.viewportContent = this.viewport.getAttribute("content");
    this.targetNode = document.querySelector('.wishcard-container');

    if (this.targetNode) {
      this.setViewportTransform(this.targetNode)

      this.observer = new MutationObserver((e) => {
        this.setViewportTransform(e[0])
      })

      this.observer.observe(this.targetNode, {
        attributes: true,
        attributeFilter: ['class'],
        childList: false,
        characterData: false
      })
    }
  },

  mounted() {
    this.updateSizes();
    this.dispatchEvent('wishcard:changed', {
      card: this.card
    });

    window.addEventListener('resize', this.onWindowResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);

    if (this.observer) {
      this.blur();
      this.observer.disconnect()
    }
  },
}
</script>

<style scoped>

.wishcard__placeholder,
.wishcard__input {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  font-family: Arial, sans-serif;
  font-style: normal;
  letter-spacing: normal;
  padding: 1rem;
  border: none;
  white-space: break-spaces;
  background-color: transparent;
  resize: none;
  box-sizing: border-box;
  overflow: hidden;
  text-align: left;
}

.wishcard__placeholder {
  z-index: 5;
  margin-top: -2px;
  opacity: .7;
}

.wishcard__input {
  z-index: 15;
}

.wishcard__validation,
.wishcard__handwritten-note {
  margin: .25rem 0 0;
  padding: 0 0 0 2px !important;
  line-height: 1.4;
  font-size: .875rem;
  font-style: italic;
  text-align: left;
}

.wishcard__validation {
  color: var(--danger);
}

.wishcard__layered-image {
  position: absolute;
  top: 0;
  right: 0;
  width: 61.5%;
  height: 100%;
  z-index: 1;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: 0;
}
</style>
